import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message } from 'antd';
import {
  Detail, Layout, List, Prompt
} from 'components/Page';
import {
  add, clear, dirty, init, list, load, remove, reset, update
} from 'actions';

let form = React.createRef();
/**
 * @description Map data and server flight
 * @param {state} Object
 */
const mapStateToProps = (state, ownProps) => ({
  item: state.api[ownProps.reducer.item.key].data,
  results: ownProps.config.list.format ? ownProps.config.list.format(state.api[ownProps.reducer.list.key].data.results) : state.api[ownProps.reducer.list.key].data.results,
  totalRecords: state.api[ownProps.reducer.list.key].data.count,
  inFlight: state.api[ownProps.reducer.list.key].inFlight,
  isDirty: state.api[ownProps.reducer.list.key].isDirty,
  formInFlight: state.api[ownProps.reducer.item.key].inFlight,
  formError: state.api[ownProps.reducer.item.key].error
});

/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 * @param {ownProps} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  create() {
    dispatch(init(ownProps.reducer.item.key, { active: true, ...ownProps.config.init }));
  },
  clearErrors() {
    dispatch(clear(ownProps.reducer.item.key));
  },
  resetItem() {
    if (ownProps.reducer.extra) {
      dispatch(reset(ownProps.reducer.extra.key));
    }
    return dispatch(reset(ownProps.reducer.item.key));
  },
  loadList(params) {
    dispatch(list(
      ownProps.reducer.list.key, params
    ));
  },
  loadItem(data) {
    const { id } = data;
    return dispatch(load(ownProps.reducer.item.key, { id }));
  },
  reloadList() {
    dispatch(dirty(ownProps.reducer.list.key));
  },
  saveItem(id, data) {
    return dispatch(id > 0 ? update(ownProps.reducer.item.key, { id, ...data }) : add(ownProps.reducer.item.key, data));
  },
  deleteItem(id) {
    return dispatch(remove(ownProps.reducer.item.key, { id }));
  }
});
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {save} Function
 * @param {cancel} Function
 */
const promptSaveChanges = (save, cancel) => (
  Prompt({
    onOkClick: save,
    onCancelClick: cancel
  })
);
/**
 * LeaveTypes
 * @description Leave Type View
 * @version 1.0.0
 */
const Standard = ({
  canAdmin,
  classes,
  config,
  clearErrors,
  create,
  deleteItem,
  formError,
  formInFlight,
  isDirty,
  item,
  results,
  inFlight,
  loadItem,
  loadList,
  messaging,
  onPrepareData,
  reloadList,
  resetItem,
  saveItem,
  themeName,
  totalRecords,
  ...rest
}) => {
  /**
   * @description Save the item and dislpay a relevant message
   * @param {id} Number
   * @param {data} Object
   * @param {prevData} Object
   * @triggers {reloadList}
   */
  const handleSave = (id, data, prevData = null) => {
    const preparedData = onPrepareData(data);
    return saveItem(id, preparedData)
      .then((response) => {
        message.success(messaging.save.success(data));
        if (prevData) {
          resetItem();
          loadItem(prevData); // load previous data
        }
        reloadList(); // reload change list data
        return response;
      },
      (error) => {
        message.error(messaging.save.failure(data));
        return error;
      });
  };
  /**
   * @description Delete the item and dislpay a relevant message
   * @param {id} Number
   * @triggers {reloadList}
   */
  const handleDelete = (id) => {
    deleteItem(id)
      .then((response) => {
        if (response.status === 204) {
          message.success(messaging.delete.success());
          resetItem(); // remove item data from store
        }
        else {
          message.success(messaging.delete.disabled());
          loadItem(response); // reload the current item to reflect the inactive field
        }
        reloadList(); // reload the list of items to reflect the changes in related  data
      })
      .catch((error) => {
        if (Array.isArray(error.allErrors) && error.allErrors.length > 0) {
          message.error(messaging.delete.failure(error.allErrors[0]));
        }
        else if (typeof error === 'string') {
          message.error(messaging.delete.failure(error));
        }
        else {
          message.error(messaging.delete.failure(''));
        }
      });
  };
  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleFormClose = (currentItem, previousItem) => {
    // Prompt to save the changed form data
    promptSaveChanges(() => handleSave(previousItem.id, previousItem, currentItem),
      () => {
      });
  };

  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleTabChange = () => {
    const { currentItem, isFormDirty } = form.state;

    // if the the form is dirty present a prompt to save
    // the form data
    if (isFormDirty) {
      form.state.isFormDirty = false; // reset form to not dirty
      promptSaveChanges(() => handleSave(currentItem.id, currentItem),
        () => loadItem(currentItem)); // reset to origin datasource
    }
  };

  /**
   * @description update form reference
   */
  const getFormRef = (input) => {
    form = input || form;
  };

  // Is the form in a state of edit or not
  const isEditMode = (item.id !== null && item.id > 0) || false;

  /**
   * @description Get List of records
   * @returns {List}
   */
  const getList = () => (
    <List
      canAdmin={canAdmin}
      className="bhp-container"
      dataSource={results}
      isDirty={isDirty}
      inFlight={inFlight}
      onLoadList={loadList}
      onAddItemClick={create}
      onRowClick={loadItem}
      onDeleteItem={handleDelete}
      onLoad={resetItem}
      selectedItemId={item.id}
      totalRecords={totalRecords}
      {...config.list}
      {...rest}
    />
  );

  /**
   * @description Get Detail tabs
   * @returns Detail
   */
  const getDetail = () => (
    <Detail
      {...config.detail}
      currentItem={item}
      isEditMode={isEditMode}
      onTabChange={handleTabChange}
      tabPosition="left"
      tabItems={config.detail.tabItems
        .filter(tab => !tab.visible || tab.visible(item))
        .map((tab) => {
        const {
          forceRender, key, title, component, closeOnSave
        } = tab;
        // setup props obejct
        const props = {
          canAdmin,
          closeOnSave,
          commands: {
            ...config.detail.commands
          },
          dataSource: item,
          formError,
          inFlight: formInFlight,
          isEditMode,
          itemId: item.id, // backwards compatiblity
          objectId: item.id,
          onSaveClick: handleSave,
          onFormClose: handleFormClose,
          onCancelClick: () => {
            clearErrors();
            resetItem();
          },
          themeName,
          ...rest
        };
        props.ref = !key ? getFormRef : undefined; // Main for needs a ref
        return ({
          key,
          title,
          component: component(props),
          forceRender
        });
      })}
    />
  );
  // Layout Component to build out the Page
  return (
    <Layout
      classes={classes}
      component1={getList()}
      component2={getDetail()}
    />
  );
};

Standard.propTypes = {
  canAdmin: PropTypes.bool,
  classes: PropTypes.string,
  config: PropTypes.object,
  clearErrors: PropTypes.func,
  create: PropTypes.func,
  deleteItem: PropTypes.func,
  formError: PropTypes.object,
  isDirty: PropTypes.bool,
  formInFlight: PropTypes.bool,
  item: PropTypes.object,
  results: PropTypes.array,
  inFlight: PropTypes.bool,
  loadItem: PropTypes.func,
  loadList: PropTypes.func,
  messaging: PropTypes.object,
  onPrepareData: PropTypes.func,
  resetItem: PropTypes.func,
  saveItem: PropTypes.func,
  reloadList: PropTypes.func,
  themeName: PropTypes.string,
  totalRecords: PropTypes.number
};

Standard.defaultProps = {
  canAdmin: false,
  classes: '',
  clearErrors() {
  },
  config: {},
  create() {
  },
  deleteItem() {
  },
  formError: {},
  isDirty: false,
  formInFlight: false,
  item: {},
  results: [],
  inFlight: false,
  loadItem() {
  },
  loadList() {
  },
  messaging: {
    delete: {
      disabled: () => 'Item Disabled.',
      failure: error => `Delete Failed. ${error}`,
      success: () => 'Delete Successful.'
    },
    save: {
      failure: data => (data.name ? `Saving '${data.name}' failed` : 'Saving Failed'),
      success: data => (data.name ? `Saving '${data.name}' was successful` : 'Saving successful')
    }
  },
  onPrepareData(data) {
    return data;
  },
  resetItem() {
  },
  saveItem() {
  },
  reloadList() {
  },
  themeName: '',
  totalRecords: 0
};

export default connect(mapStateToProps, mapDispatchToProps)(Standard);
